import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormHelperText,
    IconButton,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React, {useMemo, useState, useCallback, useRef, useEffect} from "react";
import {LoadingButton} from "@mui/lab";
import {useMutation, useQuery} from "react-query";
import {Folio, FolioPayment, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItemService} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import ProviderFactory from "./providers/ProviderFactory";
import {useAvailableBillingProviders} from "crqlar-react-common/dist/utils/hooks/billing/useAvailableBillingProviders";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import AmountField from "crqlar-react-common/dist/components/AmountField";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {addMinutes, diffInMinutes} from "crqlar-react-common/dist/utils/date.util";
import {Service} from "crqlar-react-common/models/service/service.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import CheckIcon from '@mui/icons-material/Check';
import PaymentIcon from '@mui/icons-material/Payment';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import UndoIcon from '@mui/icons-material/Undo';
import {canCapturePreauthorizedFolio, canEditFolioItems, canRefund, canReleaseFunds} from "crqlar-react-common/dist/utils/folio.util";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import TaxRatePicker from "crqlar-react-common/dist/components/TaxRatePicker";
import {useSnackbar} from 'notistack'
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {useAllServices, useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useBookingEngineConfigs} from "crqlar-react-common/dist/utils/hooks/bookingEngine/useBookingEngineConfigs";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {RateCalculationService} from "crqlar-react-common/dist/services/rate/rateCalculation.service";
import {findRateByDate} from "crqlar-react-common/dist/utils/rate.util";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {useCalculateRates, useRates, useRatesQ} from "crqlar-react-common/dist/utils/hooks/rates/useRates";
import tr from "@mobiscroll/print/dist/src/i18n/tr";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {withNoShowFee} from "./folioCharge.util";

// Marks a folio item whose VAT was explicitly set to 0% by staff (no tax rate exists for the
// service) so the Casablanca charge can carry a deliberate rate. Used to recognise + revert the stamp.
const ZERO_VAT_NOTE_NAME = 'CASABLANCA_EXPLICIT_ZERO_VAT';

interface Props extends DialogProps {
    onClose: () => void;
    onSuccess?: (folio: Folio) => void;
    reservation?: Reservation;
    reservationGroup?: ReservationGroup;
    folioId?: string
    service?: Service;
}

const SimpleChargeDialog = (props: Props) => {
    const {t} = useLocalize();
    const {onClose, onSuccess, reservation, reservationGroup, service, ...dialogProps} = props;
    const organization = useOrganization();
    const {enqueueSnackbar} = useSnackbar()

    // State
    const [selectedProvider, setSelectedProvider] = useState<string | null>("no_charge");
    const [providerData, setProviderData] = useState<any>(null);
    const [isProviderDataValid, setIsProviderDataValid] = useState(true);
    const [existingFolioFound, setExistingFolioFound] = useState<boolean>(false);
    const [chargeOptionsVisible, setChargeOptionsVisible] = useState<boolean>(false);

    // Confirmation dialog states
    const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
    const [showMarkAsPaidDialog, setShowMarkAsPaidDialog] = useState(false);
    const [showMarkAsUncollectableDialog, setShowMarkAsUncollectableDialog] = useState(false);
    const [showVoidDialog, setShowVoidDialog] = useState(false);
    const [showCapturePreauthorizedDialog, setShowCapturePreauthorizedDialog] = useState(false);
    const [showReleaseFundsDialog, setShowReleaseFundsDialog] = useState(false);
    const [showRefundDialog, setShowRefundDialog] = useState(false);
    const [refundAmount, setRefundAmount] = useState<number>(0);

    const [folioItems, setFolioItems] = useState<FolioItem[]>([]);
    // A package folio has one priced row per package service (plus extras) and no row priced from `service`:
    // every row shows its own name/price, and the total is the sum of the rows.
    const isServicePackage = !!service?.isPackage;
    const packageChildItemIdsRef = useRef<Set<string>>(new Set());
    // Folio items whose VAT we've already auto-defaulted from the service rate, so we do it once and
    // never re-apply it after the user clears the picker (clearing must stay cleared).
    const taxDefaultedItemIdsRef = useRef<Set<string>>(new Set());
    // Ids of folio items that staff explicitly stamped with a 0% VAT (because no tax rate exists),
    // so we can show the toggle as "on" and revert the stamp if they switch it back off.
    const [zeroVatItemIds, setZeroVatItemIds] = useState<string[]>([]);

    // Extract currencies to check how many different ones are used (lines without one don't count)
    // Inclusive (package member) lines carry no price, so they must not count as a separate currency
    const currencies = folioItems.filter(item => !item.inclusive).map(item => item.price?.currency).filter(Boolean)

    // Discount dialog states
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
    const [discountDialogIndex, setDiscountDialogIndex] = useState<number>(-1);
    const [discountDialogItem, setDiscountDialogItem] = useState<FolioItem | null>(null);
    const [discountType, setDiscountType] = useState<'amount' | 'percentage'>('amount');
    const [discountValue, setDiscountValue] = useState<string>('');

    const startDate = useMemo(() => reservation?.expectedStartDate || new Date(), [reservation?.expectedStartDate]);
    const serviceRates = useCalculateRates((service?.rateIds ?? []) as string[], startDate).data || []

    // Find the correct rate according to the date, fallback to the first one
    const targetRate = findRateByDate(serviceRates, reservation?.expectedStartDate || new Date());

    // The *calculated* rate (targetRate) carries the price but not the tax rate, so read the VAT
    // from the raw rate document (same id set) to default the folio item's tax rate. Only when this
    // dialog was opened for a concrete service — otherwise we must not invent a VAT for the item.
    const serviceRateIds = (service?.rateIds ?? []) as string[];
    const rawServiceRates = useRates(serviceRateIds);
    const targetRawRate = serviceRateIds.length > 0
        ? findRateByDate(rawServiceRates, reservation?.expectedStartDate || new Date())
        : undefined;


    // Use entity hook to manage folio state
    const folioData = useEntity<Folio>({
        get: async () => {
            return FolioService.getById(props.folioId!)
        },
        create: FolioService.create,
        update: FolioService.update,
        onSaved: (folio) => {
            onSuccess?.(folio);
        }
    }, props.folioId);

    // Get the current folio and setter
    const folio: Folio | undefined = folioData.item;
    const setFolio = folioData.setItem;

    useEffect(() => {
        packageChildItemIdsRef.current = new Set();
        taxDefaultedItemIdsRef.current = new Set();
        setZeroVatItemIds([]);
    }, [folio?.id]);

    // Fetch folio items when the folio ID changes or when we need to refresh
    const folioItemsQuery = useQuery(['folioItems', folio?.id], async () => {
        if (folio?.id) {
            const items = await FolioItemService.getAllFolioItems({
                folioIds: [folio.id],
                limit: {
                    offset: 0,
                    limit: 100,
                    sort: [{field: 'id', direction: 'ASC'}]
                },
            });
            const itemsContent = items.content || [];
            if (service && !service.isPackage && itemsContent.length === 1 && !itemsContent[0].description) {
                const prePopulated = {
                    ...itemsContent[0],
                    description: service.name ?? '',
                    serviceInventoryCode: service.marketCode ?? '',
                    // Carry the serviceId so the backend can attribute the charge to the right
                    // Casablanca commodity group (and resolve VAT from the service/category rate).
                    serviceId: service.id ?? itemsContent[0].serviceId,
                };
                setFolioItems([prePopulated]);
                FolioItemService.updateFolioItem(prePopulated);
            } else {
                if (service?.isPackage && packageChildItemIdsRef.current.size === 0 && itemsContent.length > 1) {
                    packageChildItemIdsRef.current = new Set(
                        itemsContent.slice(1).map(i => i.id).filter((id): id is string => !!id)
                    );
                }
                setFolioItems(itemsContent);
            }
        } else {
            setFolioItems([])
            // Empty array as default items now get created automatically when folio is created
            return [];
        }
    }, {
        enabled: props.open && !!folio?.id,
        refetchOnWindowFocus: false
    });

    // Calculate total for the folio
    const totalQuery = useQuery(['folioTotal', folio?.id, folio?.status], async () => {
        if (folio?.id && folio?.status === FolioStatus.DRAFT) {
            return FolioService.calculateTotal({id: folio.id});
        } else {
            return folio?.total;
        }
    }, {
        enabled: !!folio?.id,
        refetchOnWindowFocus: false
    });

    // Queries and Mutations
    const providersQuery = useAvailableBillingProviders();
    const providers = ["no_charge", ...(providersQuery.data || [])];

    // Create or finalize folio mutation
    const createFolioMutation = useMutation(
        'createFolio',
        async () => {
            let currentFolio = folio;

            const total = await FolioService.calculateTotal({id: currentFolio?.id!});

            // Create payment if provider is selected
            if (selectedProvider && selectedProvider !== "no_charge") {
                const payment: FolioPayment = {
                    provider: selectedProvider,
                    amount: total,
                    transactionDate: new Date().toISOString().split('T')[0],
                    paymentMethodId: providerData?.paymentMethodId,
                    email: providerData?.email,
                    firstName: providerData?.firstName,
                    lastName: providerData?.lastName,
                    sendEmail: providerData?.sendEmail,
                    chargeImmediately: providerData?.chargeImmediately,
                    roomId: providerData?.roomId,
                    externalUserId: providerData?.externalUserId,
                    externalReservationId: providerData?.externalReservationId,
                    profileId: providerData?.profileId,
                    reservationId: providerData?.reservationId
                };

                return FolioService.chargeFolio({
                    id: currentFolio?.id!,
                    payment
                });
            }
        },
        {
            onSuccess: (updatedFolio) => {
                if (updatedFolio) {
                    if (updatedFolio.status === FolioStatus.OPEN && selectedProvider !== "no_charge") {
                        // Charging failed, folio is still in OPEN state
                        let errorMessage = t('Charging failed');

                        if (selectedProvider === 'asa' && updatedFolio.folioProviderData) {
                            try {
                                const providerData = JSON.parse(updatedFolio.folioProviderData);
                                if (providerData.success === false && providerData.error?.value) {
                                    errorMessage = providerData.error.value;
                                }
                            } catch (e) {
                                console.error('Failed to parse folioProviderData:', e);
                            }
                        }

                        if (selectedProvider === 'casablanca' && updatedFolio.folioProviderData) {
                            try {
                                const providerData = JSON.parse(updatedFolio.folioProviderData);
                                if (providerData.success === false && providerData.error) {
                                    // providerData.error is an i18n key (e.g. "casablanca.guest.not.checked.in");
                                    // providerData.message carries the concrete reason from the server.
                                    errorMessage = t(providerData.error) || providerData.error;
                                    if (providerData.message) errorMessage = `${errorMessage} — ${providerData.message}`;
                                }
                            } catch (e) {
                                console.error('Failed to parse casablanca folioProviderData:', e);
                            }
                        }

                        enqueueSnackbar(errorMessage, {variant: 'error'})
                        setFolio(updatedFolio);
                    } else {
                        // Success - folio status changed or no charge
                        setFolio(updatedFolio);
                        onSuccess?.(updatedFolio);
                        setChargeOptionsVisible(false);
                        enqueueSnackbar(t("Charge success"), {variant: 'success'});
                    }
                }
            }
        }
    );

    const ROOM_CHARGE_PROVIDERS = ['asa', 'fias'];
    const isRoomChargeProvider = selectedProvider
        ? ROOM_CHARGE_PROVIDERS.includes(selectedProvider.toLowerCase())
        : false;

    const activeStayQuery = useQuery(
        ['activeStay', folio?.profileId],
        () => ProfileService.getActiveStay(folio!.profileId!),
        {
            enabled: isRoomChargeProvider && !!folio?.profileId,
            refetchOnWindowFocus: false
        }
    );
    const activeStayData = activeStayQuery.data;
    const guestHasNoActiveStay = isRoomChargeProvider && !!folio?.profileId
        && !activeStayQuery.isLoading
        && activeStayData?.hasActiveStay === false;

    const sectorId = reservation?.sectorId ?? service?.sectorIds?.[0]
    const services = useServicesForSector(sectorId)
    // Find the no show service for the current sector
    // When clicking on "No Show" status -> "No show charge", we want to auto-fill the folio item with the no show service
    const noShowServiceCurrentSector = services.filter(s => s.noShowService && s.sectorIds?.includes(reservation?.sectorId || ''))?.[0]

    const bookingEngineConfig = useBookingEngineConfigs(service?.sectorIds)?.[0]
    const chargePerPersonAmount = bookingEngineConfig?.billingConfig?.chargePerPersonAmount
    const minNumberOfPeopleForNoShowCharge = bookingEngineConfig?.billingConfig?.minNumberOfPeopleForCharge
    const totalGuestCount = reservation?.guestCounts?.reduce((sum, gc) => sum + (gc.count ?? 0), 0) ?? 0

    // Shows the charge per person amount * guest counts
    // Fallback to the totalChargeAmount in config (min amount of people to hit the charge * amount per person)
    const getTotalNoShowFee = () => {
        // Check whether min number of people condition is met
        if (totalGuestCount < (minNumberOfPeopleForNoShowCharge as number)) return 0

        return (chargePerPersonAmount && totalGuestCount)
            ? chargePerPersonAmount * totalGuestCount
            : bookingEngineConfig?.billingConfig?.totalChargeAmount
    }

    // No-show charges: the fee isn't a rate price, so save it on the first line (with the no-show
    // service's name and market code) — the charge uses the stored total. Written once per folio and
    // fee (ref), after the items have loaded; a line that already carries it is left alone.
    const noShowFeeSavedRef = useRef<string>();
    const noShowFee = reservation?.status === ReservationStatus.NO_SHOW ? getTotalNoShowFee() : undefined;
    useEffect(() => {
        const firstItem = folioItems[0];
        if (!props.open || !noShowFee || noShowFee <= 0) return;
        // A package's first row is one of its services, not a line the no-show fee may take over
        if (isServicePackage) return;
        if (!folio?.id || folio.id !== props.folioId || !canEditFolioItems(folio)) return;
        if (folioItemsQuery.isFetching || !firstItem?.id || firstItem.folioId !== folio.id) return;
        const saveKey = `${folio.id}:${noShowFee}:${noShowServiceCurrentSector?.id ?? ''}`;
        if (noShowFeeSavedRef.current === saveKey) return;

        const noShowItem = withNoShowFee(firstItem, noShowFee, organization?.currencyCode || 'EUR', noShowServiceCurrentSector);
        if (!noShowItem) return;

        noShowFeeSavedRef.current = saveKey;
        const saveNoShowFee = async () => {
            await FolioItemService.updateFolioItem(noShowItem);
            folioItemsQuery.refetch();
            totalQuery.refetch();
        };
        saveNoShowFee();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.folioId, folio?.id, folio?.status, folioItemsQuery.isFetching, folioItems, noShowFee, noShowServiceCurrentSector?.id]);

    // Handlers
    const handleProviderDataChange = (isValid: boolean, data: any) => {
        setIsProviderDataValid(isValid);
        setProviderData(data);
    };

    const addFolioItem = async (preselectedFolioItem?: FolioItem) => {
        const newItem = {
            timeSpanStart: preselectedFolioItem?.timeSpanStart ?? new Date(),
            timeSpanEnd: preselectedFolioItem?.timeSpanEnd ?? new Date(),
            description: preselectedFolioItem?.description ?? "",
            serviceInventoryCode: preselectedFolioItem?.serviceInventoryCode ?? "",
            price: preselectedFolioItem?.price ?? {
                currency: organization?.currencyCode || "EUR",
            },
            folioId: preselectedFolioItem?.id ?? folio.id
        } as FolioItem;

        // If we have a folio ID, create the item directly
        if (folio.id) {
            await FolioItemService.createFolioItem(newItem);
            folioItemsQuery.refetch();
            totalQuery.refetch();
        } else {
            // Otherwise, create a new folio first, then add the item
            const savedFolio = await folioData.save();
            if (savedFolio?.id) {
                await FolioItemService.createFolioItem({
                    ...newItem,
                    folioId: savedFolio.id
                });
                folioItemsQuery.refetch();
                totalQuery.refetch();
            }
        }
    };

    const removeFolioItem = async (index: number, item: FolioItem) => {
        if (folioItems.length > 1 && item.id) {
            await FolioItemService.deleteFolioItem({id: [item.id]});
            folioItemsQuery.refetch();
            totalQuery.refetch();
        }
    };

    const updateFolioItem = async (index: number, field: keyof FolioItem, value: any, item: FolioItem) => {
        if (item.id) {
            // If item already exists in database, update it
            const updatedItem = {
                ...item,
                [field]: value
            };

            const newFolioItems = [...folioItems]
            newFolioItems[index] = updatedItem
            setFolioItems(newFolioItems)

            FolioItemService.updateFolioItem(updatedItem);
            totalQuery.refetch();
        } else if (folio.id) {
            // If folio exists but item doesn't, create the item
            const newItem = {
                ...item,
                [field]: value,
                folioId: folio.id
            };

            setFolioItems([...folioItems, newItem])

            await FolioItemService.createFolioItem(newItem);
            folioItemsQuery.refetch();
            totalQuery.refetch();
        }
        // If neither folio nor item exist, we'll handle this when saving the folio
    };

    const updateFolioItemMultipleFields = async (
        index: number,
        fields: Partial<FolioItem> | [keyof FolioItem, any][],
        item: FolioItem
    ) => {
        // Convert the input to a consistent format (object with fields to update)
        let fieldsToUpdate: Partial<FolioItem>;

        if (Array.isArray(fields)) {
            // If fields is an array of [key, value] pairs, convert to object
            fieldsToUpdate = fields.reduce((obj, [field, value]) => {
                obj[field] = value;
                return obj;
            }, {} as Partial<FolioItem>);
        } else {
            // If fields is already an object, use it directly
            fieldsToUpdate = fields;
        }

        if (item.id) {
            // If item already exists in database, update it
            const updatedItem = {
                ...item,
                ...fieldsToUpdate
            };

            setFolioItems(prev => {
                const newFolioItems = [...prev];
                newFolioItems[index] = {...prev[index], ...fieldsToUpdate};
                return newFolioItems;
            });

            FolioItemService.updateFolioItem(updatedItem);
            totalQuery.refetch();
        } else if (folio.id) {
            // If folio exists but item doesn't, create the item
            const newItem = {
                ...item,
                ...fieldsToUpdate,
                folioId: folio.id
            };

            setFolioItems([...folioItems, newItem]);

            await FolioItemService.createFolioItem(newItem);
            folioItemsQuery.refetch();
            totalQuery.refetch();
        }
        // If neither folio nor item exist, we'll handle this when saving the folio
    };

    // Default the first item's VAT from the service rate — parity with how its price/currency are
    // taken from the rate (targetRate). Staff usually never open the VAT picker, so without this the
    // charge goes out with no tax rate. Applied at most once per item (tracked in the ref), so we
    // never overwrite a manual choice and a cleared picker stays cleared. Skips no-show charges,
    // whose fee/tax don't come from this rate.
    useEffect(() => {
        if (!folio || !canEditFolioItems(folio)) return;
        if (reservation?.status === ReservationStatus.NO_SHOW) return;
        // Package rows are built with their own service's VAT; the package rate must not override row 1.
        if (isServicePackage) return;
        const firstItem = folioItems[0];
        if (!firstItem?.id || taxDefaultedItemIdsRef.current.has(firstItem.id)) return;
        // Already has a VAT (saved or just defaulted) -> remember it and never auto-touch it again,
        // so a later manual clear isn't bounced back to the default.
        if (firstItem.taxRateId) {
            taxDefaultedItemIdsRef.current.add(firstItem.id);
            return;
        }
        const rateTaxRateId = targetRawRate?.taxRateId ?? targetRawRate?.taxRateSnap?.id;
        if (rateTaxRateId) {
            taxDefaultedItemIdsRef.current.add(firstItem.id);
            updateFolioItem(0, 'taxRateId', rateTaxRateId, firstItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetRawRate?.taxRateId, targetRawRate?.taxRateSnap?.id, folioItems[0]?.id, folioItems[0]?.taxRateId, folio?.status]);

    // Chargeable, non-inclusive items that have no VAT of their own (no tax rate id, no snapshot).
    // Once stamped with an explicit 0% (below) they gain a snapshot and drop out of this list.
    const itemsMissingVat = folioItems.filter(it =>
        !it.archived && !it.inclusive && it.taxRateId == null && it.taxRateSnap?.rate == null
    );
    const zeroVatActive = zeroVatItemIds.length > 0;

    // Casablanca requires an explicit tax rate on every line. When no rate exists for a service,
    // staff can deliberately send a 0% — we stamp a real 0% snapshot on each affected item (which
    // the backend transmits verbatim) plus an audit note, and revert both when toggled back off.
    const handleToggleZeroVat = async (enabled: boolean) => {
        if (enabled) {
            const stamped: string[] = [];
            for (let index = 0; index < folioItems.length; index++) {
                const it = folioItems[index];
                if (!it.id || it.archived || it.inclusive) continue;
                if (it.taxRateId != null || it.taxRateSnap?.rate != null) continue;
                const notes = it.notes ?? [];
                const withNote = notes.some(n => n.name === ZERO_VAT_NOTE_NAME)
                    ? notes
                    : [...notes, {
                        name: ZERO_VAT_NOTE_NAME,
                        note: 'Charged with explicit 0% VAT — no tax rate configured for this service; confirmed by staff.',
                        new: true,
                    }];
                await updateFolioItemMultipleFields(index, {
                    taxRateSnap: {rate: 0, inclusive: false},
                    notes: withNote,
                }, it);
                stamped.push(it.id);
            }
            setZeroVatItemIds(stamped);
        } else {
            for (let index = 0; index < folioItems.length; index++) {
                const it = folioItems[index];
                if (!it.id || !zeroVatItemIds.includes(it.id)) continue;
                await updateFolioItemMultipleFields(index, {
                    taxRateSnap: undefined,
                    notes: (it.notes ?? []).filter(n => n.name !== ZERO_VAT_NOTE_NAME),
                }, it);
            }
            setZeroVatItemIds([]);
        }
    };

    const handleSaveAndCharge = () => {
        createFolioMutation.mutate();
    };

    const handleSaveAndPrint = async () => {
        if (!folio?.id) {
            // Save folio first if it doesn't exist
            const savedFolio = await folioData.save();

            // Then create items if needed
            if (savedFolio?.id && folioItems.length > 0 && !folioItems[0].id) {
                const itemPromises = folioItems.map(item => {
                    return FolioItemService.createFolioItem({
                        ...item,
                        folioId: savedFolio.id
                    });
                });
                await Promise.all(itemPromises);
            }

            if (savedFolio?.id) {
                window.open(`/print/folio/${savedFolio.id}`, '_blank');
            }
        } else {
            // Folio already exists, just open print window
            window.open(`/print/folio/${folio.id}`, '_blank');
        }
    };

    // Folio action handlers
    const handleFinalize = async () => {
        if (folio?.id) {
            try {
                if (folioData.isDirty) {
                    await folioData.save();
                }
                await FolioService.finalize({id: folio.id});
                const updatedFolio = await FolioService.getById(folio.id);
                setFolio(updatedFolio);
                onSuccess?.(updatedFolio);
            } catch (error) {
                console.error("Error finalizing folio:", error);
            }
            setShowFinalizeDialog(false);
        }
    };

    const handleMarkAsPaid = async () => {
        if (folio?.id) {
            try {
                await FolioService.markAsPaid({id: folio.id});
                const updatedFolio = await FolioService.getById(folio.id);
                setFolio(updatedFolio);
                onSuccess?.(updatedFolio);
            } catch (error) {
                console.error("Error marking folio as paid:", error);
            }
            setShowMarkAsPaidDialog(false);
        }
    };

    const handleMarkAsUncollectable = async () => {
        if (folio?.id) {
            try {
                await FolioService.markAsUncollectable({id: folio.id});
                const updatedFolio = await FolioService.getById(folio.id);
                setFolio(updatedFolio);
                onSuccess?.(updatedFolio);
            } catch (error) {
                console.error("Error marking folio as uncollectable:", error);
            }
            setShowMarkAsUncollectableDialog(false);
        }
    };

    const handleVoidFolio = async () => {
        if (folio?.id) {
            try {
                await FolioService.void({id: folio.id});
                const updatedFolio = await FolioService.getById(folio.id);
                setFolio(updatedFolio);
                onSuccess?.(updatedFolio);
            } catch (error) {
                console.error("Error voiding folio:", error);
            }
            setShowVoidDialog(false);
        }
    };

    const handleCapturePreauthorized = async () => {
        if (folio?.id) {
            try {
                const response = await FolioService.capturePreauthorized({id: folio.id});
                if (response.status === FolioStatus.PAID) {
                    alert('Payment captured successfully');
                } else {
                    alert('Payment not captured');
                }
                setFolio(response);
                onSuccess?.(response);
            } catch (error) {
                console.error("Error capturing preauthorized payment:", error);
                alert("Error capturing preauthorized payment");
            }
            setShowCapturePreauthorizedDialog(false);
        }
    };

    // Discount handlers
    const handleDiscountClick = (index: number, item: FolioItem) => {
        const existingDiscount = item.discounts?.[0];

        setDiscountDialogIndex(index);
        setDiscountDialogItem(item);

        if (existingDiscount) {
            // If discount exists, initialize dialog state with existing values
            setDiscountType(existingDiscount.percentOff !== undefined && existingDiscount.percentOff !== null ? 'percentage' : 'amount');
            setDiscountValue(
                existingDiscount.percentOff !== undefined && existingDiscount.percentOff !== null
                    ? existingDiscount.percentOff.toString()
                    : (existingDiscount.amountCentsOff !== undefined ? (existingDiscount.amountCentsOff / 100).toString() : '')
            );
        } else {
            // Initialize with default values
            setDiscountType('amount');
            setDiscountValue('');
        }

        setDiscountDialogOpen(true);
    };

    const handleDiscountDialogClose = () => {
        setDiscountDialogOpen(false);
        setDiscountDialogIndex(-1);
        setDiscountDialogItem(null);
        setDiscountType('amount');
        setDiscountValue('');
    };

    const handleDiscountApply = async () => {
        if (discountDialogIndex === -1 || !discountDialogItem) return;

        const discountValueNum = parseFloat(discountValue || '0');
        if (discountValueNum <= 0) return;

        // Validate percentage is between 0 and 100
        if (discountType === 'percentage' && (discountValueNum < 0 || discountValueNum > 100)) {
            return;
        }

        // Validate amount doesn't exceed item price
        if (discountType === 'amount') {
            const itemPrice = discountDialogItem.price?.amount || 0;
            if (discountValueNum > itemPrice) {
                return;
            }
        }

        const discount = discountType === 'percentage'
            ? {percentOff: discountValueNum}
            : {amountCentsOff: Math.floor(discountValueNum * 100)};

        const updatedDiscounts = [discount];
        await updateFolioItem(discountDialogIndex, 'discounts', updatedDiscounts, discountDialogItem);
        handleDiscountDialogClose();
    };

    const handleDiscountRemove = async () => {
        if (discountDialogIndex === -1 || !discountDialogItem) return;

        await updateFolioItem(discountDialogIndex, 'discounts', [], discountDialogItem);
        handleDiscountDialogClose();
    };

    // Helper function to format discount display
    const formatDiscountDisplay = (item: FolioItem) => {
        const discount = item.discounts?.[0];
        if (!discount || !discount.percentOff && !discount.amountCentsOff) return t('+ Discount');

        if (discount.percentOff) {
            return `-${discount.percentOff}%`;
        } else if (discount.amountCentsOff) {
            const amount = (discount.amountCentsOff / 100).toFixed(2);
            return `-${amount} ${organization?.currencyCode || 'EUR'}`;
        }

        return t('Discount');
    };

    // Calculate totals from the stored items — the charge uses the stored total
    const calculateSubtotal = () => {
        // A package's rows are each priced on their own, so every row (the first included) counts as stored
        return folioItems.reduce((total, item) => {
            const basePrice = item.price?.amount || 0;
            const discount = item.discounts?.[0];

            if (!discount) {
                return total + basePrice;
            }

            let discountAmount = 0;
            if (discount.percentOff) {
                // Percentage discount
                discountAmount = basePrice * (discount.percentOff / 100);
            } else if (discount.amountCentsOff) {
                // Fixed amount discount (convert from cents to currency units)
                discountAmount = discount.amountCentsOff / 100;
            }

            // Apply discount but ensure price doesn't go below 0
            const finalPrice = Math.max(0, basePrice - discountAmount);
            return total + finalPrice;
        }, 0);
    };

    // Additional items (index > 0) require both description and price.
    // On a package every row is a service of its own, so every chargeable row needs a price.
    const additionalItemsValid = isServicePackage
        ? folioItems.filter(item => !item.inclusive)
            .every(item => !!item.description && item.price?.amount != null && item.price.amount > 0)
        : folioItems.slice(1).every(item => !!item.description && item.price?.amount != null && item.price.amount > 0);

    const canSave = folioItems.every(item => item.description) &&
        additionalItemsValid &&
        (selectedProvider === "no_charge" || (selectedProvider && isProviderDataValid));

    if (!folio) return null

    return (
        <Dialog
            {...dialogProps}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
                <DialogTitle>
                    {isServicePackage ? `${t('Folio')} · ${t('Package')}: ${service?.name ?? ''}` : t('Folio')}
                </DialogTitle>

                {folio.id && folio.status && (
                    <Alert
                        severity={
                            folio.status === FolioStatus.DRAFT ? "info" :
                                folio.status === FolioStatus.OPEN ? "warning" :
                                    folio.status === FolioStatus.PAID ? "success" :
                                        folio.status === FolioStatus.VOID ||
                                        folio.status === FolioStatus.UNCOLLECTABLE ? "error" : "info"
                        }
                        sx={{mb: 2}}
                    >
                        {t('Folio status')}: {t(`Folio status ${folio.status}`)}
                    </Alert>
                )}

            </Stack>


            <DialogContent>
                <Stack spacing={3} sx={{pt: 1}}>
                    {existingFolioFound && folio.id && (
                        <Alert
                            severity="info"
                            sx={{mb: 2}}
                            onClose={() => setExistingFolioFound(false)}
                        >
                            {t('Found existing draft folio')} #{folio.id}. {t('You can continue editing this folio.')}
                        </Alert>
                    )}

                    {folioItems.map((item, index) => {
                        // Row 1 normally mirrors the dialog's service/rate. A package has no such row: each row is
                        // one package service (or extra) with its own name and price.
                        const isFirstItem = index === 0 && !isServicePackage;
                        // Only hide legacy member rows of folios that can no longer be converted (not DRAFT):
                        // they carry no price and would otherwise show up as invalid, unpriced charges.
                        if (index > 0 && isServicePackage && !canEditFolioItems(folio) && item.id
                            && packageChildItemIdsRef.current.has(item.id)
                            && !item.inclusive && !item.price?.amount) return null;
                        return (
                        <Stack key={item.id || index} direction="row" spacing={2} sx={{mb: 2}}>

                            <Box sx={{width: 200}}>
                                <CDatePicker
                                    label={t('Date')}
                                    id={`date-${index}`}
                                    value={item.timeSpanStart}
                                    onChange={(date) => updateFolioItem(index, 'timeSpanStart', date, item)}
                                    sx={{height: 20}}
                                    disabled={!canEditFolioItems(folio) || isFirstItem}
                                />
                            </Box>
                            <TextField
                                sx={{width: 100}}
                                size="small"
                                value={isFirstItem ? service?.durationMinutes : diffInMinutes(item.timeSpanEnd, item.timeSpanStart)}
                                onChange={(e) => {
                                    const minutes = parseInt(e.target.value) || 0
                                    const newEnd = addMinutes(item.timeSpanStart, minutes)
                                    updateFolioItem(index, 'timeSpanEnd', newEnd, item)
                                }}
                                label={t('Duration (min)')}
                                disabled={!canEditFolioItems(folio) || isFirstItem}
                            />

                            {isFirstItem ? (
                                <TextField
                                    fullWidth
                                    size={'small'}
                                    value={item.description}
                                    label={t('Service name')}
                                    disabled
                                    sx={{flexGrow: 1, maxWidth: '200px'}}
                                />
                            ) : (
                                <Autocomplete
                                    id={`service-description-autocomplete-${index}`}
                                    options={services}
                                    getOptionLabel={(option) => option.name || 'No service name'}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            {option.name}
                                        </li>
                                    )}
                                    value={services.find(s => s.name === item.description)
                                        || services.find(s => !!item.serviceId && s.id === item.serviceId)
                                        // Keep showing the row's own service even if it isn't in this sector's list
                                        || (item.description ? {id: item.serviceId, name: item.description} as Service : null)}
                                    filterOptions={(options, { inputValue }) => {
                                        const lower = inputValue.toLowerCase();
                                        return options.filter(s => s.name?.toLowerCase().includes(lower))
                                    }}
                                    fullWidth
                                    size="small"
                                    onChange={async (e, newService) => {
                                        if (newService?.id) {
                                            const selectedService = services.find(s => s.id === newService.id)
                                            if (!selectedService) return

                                            const minutes = selectedService.durationMinutes
                                            const newEnd = addMinutes(item.timeSpanStart, minutes)

                                            let price = {
                                                currency: organization?.currencyCode || 'EUR',
                                                amount: 0,
                                                amountCents: 0
                                            }

                                            let selectedTaxRateId = item?.taxRateId
                                            if (selectedService.rateIds && selectedService.rateIds.length > 0) {
                                                try {
                                                    // Price the row at the SELECTED service's rate, not the dialog's own
                                                    // service (on a package that would be the package price).
                                                    const onDate = item.timeSpanStart ? new Date(item.timeSpanStart) : startDate
                                                    const selectedRates = await RateCalculationService.calculate({
                                                        ids: selectedService.rateIds as string[],
                                                        onDate
                                                    })
                                                    const selectedRate = findRateByDate(selectedRates, onDate)

                                                    if (selectedRate?.price?.amount !== undefined) {
                                                        price.amount = selectedRate.price.amount;
                                                        // The backend derives the folio item's totalAmount from `amountCents`
                                                        // (and even re-derives `amount` from it), so an implicitly-filled price
                                                        // that carries only `amount` posts as a 0 charge — e.g. nothing reaches
                                                        // Casablanca. Always set `amountCents` alongside `amount`.
                                                        price.amountCents = selectedRate.price.amountCents
                                                            ?? Math.round(selectedRate.price.amount * 100);
                                                    }
                                                    selectedTaxRateId = selectedRate?.taxRateId ?? selectedTaxRateId
                                                } catch (error) {
                                                    console.error('Error calculating rate:', error);
                                                }
                                            }

                                            updateFolioItemMultipleFields(
                                                index,
                                                [
                                                    ['description', selectedService.name],
                                                    ['serviceInventoryCode', selectedService.marketCode ?? ''],
                                                    // Attribute the line to the right commodity group / VAT source.
                                                    ['serviceId', selectedService.id],
                                                    ['timeSpanEnd', newEnd],
                                                    ['price', price],
                                                    ['taxRateId', selectedTaxRateId]
                                                ],
                                                item
                                            )
                                        } else {
                                            updateFolioItemMultipleFields(
                                                index,
                                                [
                                                    ['description', ''],
                                                    ['serviceInventoryCode', ''],
                                                    ['serviceId', undefined],
                                                    ['timeSpanEnd', item.timeSpanStart],
                                                    ['price', undefined],
                                                    ['taxRateId', item?.taxRateId]
                                                ],
                                                item
                                            )
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size={'small'}
                                            label={t('Service name')}
                                            required
                                            error={!item.description}
                                            helperText={!item.description ? t('Service name is required') : undefined}
                                        />
                                    )}
                                    disabled={!canEditFolioItems(folio)}
                                    sx={{
                                        flexGrow: 1,
                                        maxWidth: '200px'
                                    }}
                                    freeSolo={false}
                                />
                            )}

                            <TextField
                                sx={{width: 150}}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={item.serviceInventoryCode}
                                onChange={(e) => updateFolioItem(index, 'serviceInventoryCode', e.target.value, item)}
                                label={t('Market code (SKU)')}
                                disabled={!canEditFolioItems(folio) || isFirstItem}
                            />

                            {
                                !item.inclusive &&
                                <Box sx={{width: 250}}>
                                    <AmountField
                                        sx={{width: '100%'}}
                                        value={item.price}
                                        onChange={(e) => updateFolioItem(index, 'price', e || 0, item)}
                                        label={t('Price')}
                                        disabled={!canEditFolioItems(folio) || isFirstItem}
                                        error={!isFirstItem && (!item.price?.amount || item.price.amount <= 0)}
                                    />
                                    {!isFirstItem && (!item.price?.amount || item.price.amount <= 0) && (
                                        <FormHelperText error>{t('Price is required')}</FormHelperText>
                                    )}
                                </Box>
                            }

                            {canEditFolioItems(folio) && !item.inclusive && item.discounts?.[0] && (
                                <TextField
                                    variant={'outlined'}
                                    value={formatDiscountDisplay(item)}
                                    label={t('Discount')}
                                    size="small"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleDiscountClick(index, item)
                                    }}
                                    sx={{
                                        minWidth: 90,
                                        height: 40,
                                    }}
                                />
                            )}

                            {
                                !item.inclusive &&
                                <TaxRatePicker
                                    sx={{width: 100}}
                                    value={item.taxRateId}
                                    // Drop the snapshot with it: the backend resolves it from the id, and a
                                    // cleared picker must not keep charging the old VAT
                                    onChange={e => updateFolioItemMultipleFields(index, {taxRateId: e, taxRateSnap: undefined}, item)}
                                    disabled={!canEditFolioItems(folio)}
                                />
                            }

                            {
                                !!item.inclusive &&
                                <Typography
                                    sx={{
                                        width: 366,
                                        alignSelf: 'center'
                                    }}
                                >{isServicePackage ? t('Included in package') : t('Inclusive')}</Typography>
                            }

                            {canEditFolioItems(folio) && !item.inclusive && !item.discounts?.[0] && (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={(e) => handleDiscountClick(index, item)}
                                    sx={{
                                        minWidth: 90,
                                        height: 40,
                                    }}
                                    startIcon={<AddIcon/>}
                                >
                                    {t('Discount')}
                                </Button>
                            )}

                            {canEditFolioItems(folio) && !isFirstItem &&
                                <IconButton
                                    onClick={() => removeFolioItem(index, item)}
                                    sx={{width: 40}}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            }

                        </Stack>
                        );
                    })}

                    {/* No VAT rate for the service(s): Casablanca books VAT exactly as transmitted and
                        requires an explicit rate on every line, so we offer a deliberate 0% opt-in.
                        This requirement is Casablanca-specific — other providers don't block on a
                        missing per-line VAT — so only surface the warning + 0% toggle for orgs that
                        actually have Casablanca active. We key off "Casablanca is an available
                        provider" rather than "Casablanca is the selected provider": the 0% stamp edits
                        folio items, which is only possible while the folio is still editable (DRAFT),
                        whereas the provider is only chosen later in the OPEN/charge phase. */}
                    {canEditFolioItems(folio) && providers.includes('casablanca')
                        && (itemsMissingVat.length > 0 || zeroVatActive) && (
                        <Alert
                            severity={zeroVatActive ? 'info' : 'warning'}
                            action={
                                <FormControlLabel
                                    sx={{mr: 0}}
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            checked={zeroVatActive}
                                            onChange={(e) => handleToggleZeroVat(e.target.checked)}
                                        />
                                    }
                                    label={t('Charge 0% VAT')}
                                />
                            }
                        >
                            <AlertTitle>{zeroVatActive ? t('0% VAT rate set') : t('No VAT rate set')}</AlertTitle>
                            {zeroVatActive
                                ? t('Affected item(s) will be charged with an explicit 0% VAT.')
                                : t('Casablanca books VAT exactly as transmitted and requires a tax rate on every charged item — even a 0% one. Set a tax rate on the item (or its service/category rate), or switch on "Charge 0% VAT" to send a deliberate 0%.')}
                        </Alert>
                    )}


                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{width: '100%'}}>

                        {
                            canEditFolioItems(folio) &&
                            <Button
                                startIcon={<AddIcon/>}
                                onClick={addFolioItem}
                                disabled={!additionalItemsValid}
                            >
                                {t('Add Item')}
                            </Button>
                        }

                        <Typography variant="subtitle1">
                            {t('Subtotal')}: {new Set(currencies)?.size > 1
                            ? t('Multiple currencies involved')!
                            : `${calculateSubtotal().toFixed(2)} ${organization?.currencyCode || "EUR"}`}
                        </Typography>
                    </Stack>


                    <Stack direction={'row'} width={'100%'} justifyContent={'flex-start'} flexWrap={'wrap'} gap={2}>
                        {/* Action buttons based on folio status */}
                        {(!folio.id || folio.status == FolioStatus.DRAFT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowFinalizeDialog(true)}
                                startIcon={<CheckIcon/>}
                                disabled={!additionalItemsValid}
                            >
                                {t('Finalize')}
                            </Button>
                        )}

                        {folio.id && canCapturePreauthorizedFolio(folio as Folio) && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setShowCapturePreauthorizedDialog(true)}
                                startIcon={<PaymentIcon/>}
                            >
                                {t('Charge preauthorized')}
                            </Button>
                        )}

                        {folio.id && canReleaseFunds(folio as Folio) && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setShowReleaseFundsDialog(true)}
                                startIcon={<MoneyOffIcon/>}
                            >
                                {t('Release funds')}
                            </Button>
                        )}

                        {folio.id && canRefund(folio as Folio) && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                    setRefundAmount(totalQuery.data?.amount || 0)
                                    setShowRefundDialog(true)
                                }}
                                startIcon={<UndoIcon/>}
                            >
                                {t('Refund')}
                            </Button>
                        )}

                        {/* Charge action */}
                        {folio.id && folio.status === FolioStatus.OPEN && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PaymentIcon/>}
                                onClick={() => {
                                    setChargeOptionsVisible(true);
                                }}
                            >
                                {t('Charge')}
                            </Button>
                        )}

                        {/* Print button - always available */}
                        <LoadingButton
                            variant="outlined"
                            color="primary"
                            onClick={handleSaveAndPrint}
                            disabled={!canSave}
                            loading={createFolioMutation.isLoading}
                            startIcon={<PrintIcon/>}
                            sx={{ml: 'auto'}}
                        >
                            {t('Print')}
                        </LoadingButton>
                    </Stack>

                    <Divider/>

                    {folio.id && chargeOptionsVisible && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle2" sx={{mt: 2}}>
                                {t('Payment Options')}
                            </Typography>

                            <ToggleButtonGroup
                                value={selectedProvider}
                                exclusive
                                onChange={(_, value) => setSelectedProvider(value)}
                                aria-label="payment provider"
                                color="primary"
                                sx={{
                                    flexWrap: 'wrap',
                                    '& .MuiToggleButtonGroup-grouped': {
                                        margin: 0.5,
                                        flex: 1,
                                        alignSelf: 'flex-start',
                                        minWidth: '120px',
                                        border: '1px solid !important',
                                        borderRadius: '4px !important',
                                    },
                                }}
                            >
                                {providersQuery.isLoading ? (
                                    <Box sx={{p: 2, textAlign: 'center', width: '100%'}}>
                                        <Typography variant="body2">{t('Loading providers...')}</Typography>
                                    </Box>
                                ) : providers.length > 0 ? (
                                    providers.map((provider) => (
                                        <ToggleButton
                                            key={provider}
                                            value={provider}
                                            sx={{
                                                textTransform: 'none',
                                                py: 1
                                            }}
                                        >
                                            {provider === "no_charge" ? t('No Charge') : provider}
                                        </ToggleButton>
                                    ))
                                ) : (
                                    <Box sx={{p: 2, textAlign: 'center', width: '100%'}}>
                                        <Typography variant="body2">{t('No payment providers available')}</Typography>
                                    </Box>
                                )}
                            </ToggleButtonGroup>

                            {providersQuery.isError && (
                                <FormHelperText error>
                                    {t('Failed to load payment providers')}
                                </FormHelperText>
                            )}
                        </Stack>
                    )}

                    {folio.id && selectedProvider && selectedProvider !== "no_charge" && (
                        <Box>
                            {guestHasNoActiveStay && (
                                <Alert severity="warning" sx={{mb: 2}}>
                                    {t('Guest is not staying currently')}
                                </Alert>
                            )}
                            <ProviderFactory
                                provider={selectedProvider}
                                profileId={folio.profileId}
                                folio={folio}
                                onDataChange={handleProviderDataChange}
                            />
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                onClick={handleSaveAndCharge}
                                disabled={!isProviderDataValid || createFolioMutation.isLoading || guestHasNoActiveStay}
                                loading={createFolioMutation.isLoading}
                                sx={{mt: 2}}
                            >
                                {t('Charge')}
                            </LoadingButton>
                        </Box>
                    )}

                </Stack>
            </DialogContent>

            {/* Discount Dialog */}
            <Dialog
                open={discountDialogOpen}
                onClose={handleDiscountDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {discountDialogItem?.discounts?.[0] ? t('Edit Discount') : t('Add Discount')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{pt: 1}}>
                        <ToggleButtonGroup
                            value={discountType}
                            exclusive
                            onChange={(_, value) => {
                                if (value) {
                                    setDiscountType(value);
                                    setDiscountValue('');
                                }
                            }}
                            aria-label="discount type"
                            size="small"
                            sx={{width: '100%'}}
                        >
                            <ToggleButton value="amount" sx={{flex: 1}}>
                                {t('Amount')}
                            </ToggleButton>
                            <ToggleButton value="percentage" sx={{flex: 1}}>
                                {t('Percentage')}
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                            placeholder={discountType === 'percentage' ? t('Percentage off') : t('Amount off')}
                            InputProps={{
                                endAdornment: discountType === 'percentage' ? '%' : organization?.currencyCode || 'EUR'
                            }}
                            error={
                                (!!discountValue &&
                                    (
                                        (discountType === 'percentage' && (parseFloat(discountValue) < 0 || parseFloat(discountValue) > 100)) ||
                                        (discountType === 'amount' && parseFloat(discountValue) > (discountDialogItem?.price?.amount || 0))
                                    ))
                            }
                            helperText={
                                discountValue &&
                                (
                                    (discountType === 'percentage' && (parseFloat(discountValue) < 0 || parseFloat(discountValue) > 100))
                                        ? t('Percentage must be between 0 and 100')
                                        : (discountType === 'amount' && parseFloat(discountValue) > (discountDialogItem?.price?.amount || 0))
                                            ? t('Amount cannot exceed item price')
                                            : ''
                                )
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDiscountDialogClose}>
                        {t('Cancel')}
                    </Button>

                    {discountDialogItem?.discounts?.[0] && (
                        <Button
                            onClick={handleDiscountRemove}
                            color="error"
                            variant="outlined"
                        >
                            {t('Remove')}
                        </Button>
                    )}

                    <Button
                        onClick={handleDiscountApply}
                        variant="contained"
                        disabled={
                            !discountValue ||
                            parseFloat(discountValue) <= 0 ||
                            (discountType === 'percentage' && (parseFloat(discountValue) < 0 || parseFloat(discountValue) > 100)) ||
                            (discountType === 'amount' && parseFloat(discountValue) > (discountDialogItem?.price?.amount || 0))
                        }
                    >
                        {discountDialogItem?.discounts?.[0] ? t('Update') : t('Apply')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialogs */}
            <Dialog
                open={showFinalizeDialog}
                onClose={() => setShowFinalizeDialog(false)}
            >
                <DialogTitle>{t('Finalize Folio')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to finalize this folio? Once finalized, no further items can be added.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowFinalizeDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={handleFinalize}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showMarkAsPaidDialog}
                onClose={() => setShowMarkAsPaidDialog(false)}
            >
                <DialogTitle>{t('Mark as Paid')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to mark this folio as paid? This will change the folio status to PAID.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMarkAsPaidDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={handleMarkAsPaid}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showMarkAsUncollectableDialog}
                onClose={() => setShowMarkAsUncollectableDialog(false)}
            >
                <DialogTitle>{t('Mark as Uncollectable')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to mark this folio as uncollectable? This action cannot be undone.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMarkAsUncollectableDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={handleMarkAsUncollectable}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showVoidDialog}
                onClose={() => setShowVoidDialog(false)}
            >
                <DialogTitle>{t('Void Folio')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to void this folio? This action cannot be undone.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowVoidDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={handleVoidFolio}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showCapturePreauthorizedDialog}
                onClose={() => setShowCapturePreauthorizedDialog(false)}
            >
                <DialogTitle>{t('Capture preauthorized amount')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to capture preauthorized payment?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCapturePreauthorizedDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={handleCapturePreauthorized}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Release Funds Dialog */}
            <Dialog
                open={showReleaseFundsDialog}
                onClose={() => setShowReleaseFundsDialog(false)}
            >
                <DialogTitle>{t('Release Funds')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to release the preauthorized funds? This will cancel the hold on the customer\'s payment method.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowReleaseFundsDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            if (folio?.id) {
                                try {
                                    const response = await FolioService.reverse({id: folio.id});
                                    if (response.status === FolioStatus.RELEASED) {
                                        enqueueSnackbar(t('Funds released successfully'), {variant: 'success'});
                                    } else {
                                        enqueueSnackbar(t('Funds release completed'), {variant: 'success'});
                                    }
                                    setFolio(response);
                                    onSuccess?.(response);
                                } catch (e) {
                                    console.error(e);
                                    enqueueSnackbar(t('Error releasing funds'), {variant: 'error'});
                                }
                                setShowReleaseFundsDialog(false);
                            }
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Refund Dialog */}
            <Dialog
                open={showRefundDialog}
                onClose={() => setShowRefundDialog(false)}
            >
                <DialogTitle>{t('Refund')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t('Enter the amount to refund. The maximum refundable amount is')} {totalQuery.data?.amount || 0} {totalQuery.data?.currency || 'EUR'}.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('Refund Amount')}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(Math.max(0, Math.min(totalQuery.data?.amount || 0, parseFloat(e.target.value) || 0)))}
                        InputProps={{
                            endAdornment: <Typography>{totalQuery.data?.currency || 'EUR'}</Typography>,
                            inputProps: {
                                min: 0,
                                max: totalQuery.data?.amount || 0,
                                step: 0.01
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRefundDialog(false)}>{t('Cancel')}</Button>
                    <Button
                        onClick={async () => {
                            if (folio?.id) {
                                try {
                                    const amountCents = Math.round(refundAmount * 100);
                                    const response = await FolioService.refund({id: folio.id, amountCents});
                                    if (response.status === FolioStatus.REFUNDED) {
                                        enqueueSnackbar(t('Refund processed successfully'), {variant: 'success'});
                                    } else {
                                        enqueueSnackbar(t('Refund completed'), {variant: 'success'});
                                    }
                                    setFolio(response);
                                    onSuccess?.(response);
                                } catch (e) {
                                    console.error(e);
                                    enqueueSnackbar(t('Error processing refund'), {variant: 'error'});
                                }
                                setShowRefundDialog(false);
                            }
                        }}
                        disabled={refundAmount <= 0}
                        variant="contained"
                    >
                        {t('Refund')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default SimpleChargeDialog;
